import React from "react";
import { SavedTournament, StandingsRow } from "../../types.ts";
import StandingsTable from "./StandingsTable.tsx";
import ChessPiece, { ChessPieceName } from "./ChessPiece.tsx";
import "./ChessTheme.css";

interface ChampionshipBoardProps {
  standings: StandingsRow[];
  tournaments: SavedTournament[];
}

/** Figura, barva a pruh pro první tři příčky průběžného pořadí. */
const TOP_RANKS: {
  piece: ChessPieceName;
  label: string;
  pieceColor: string;
  stripe: string;
}[] = [
  {
    piece: "king",
    label: "Král šampionátu",
    pieceColor: "text-amber-300",
    stripe: "pc-rank-1",
  },
  {
    piece: "queen",
    label: "Dáma na druhé",
    pieceColor: "text-slate-200",
    stripe: "pc-rank-2",
  },
  {
    piece: "knight",
    label: "Jezdec na třetí",
    pieceColor: "text-orange-300",
    stripe: "pc-rank-3",
  },
];

/**
 * Průběžné pořadí jako šachovnice: tmavý panel s figurami pro první tři
 * příčky a pod ním kompletní tabulka.
 */
const ChampionshipBoard: React.FC<ChampionshipBoardProps> = ({
  standings,
  tournaments,
}) => {
  const topThree = standings.slice(0, 3);

  return (
    <section>
      <div className="overflow-hidden rounded-3xl border border-slate-200 shadow-sm">
        <div className="relative bg-gradient-to-br from-slate-900 via-slate-800 to-indigo-900 text-white">
          <div className="pc-board-dark absolute inset-0" aria-hidden="true" />
          <ChessPiece
            name="rook"
            className="pc-float-slow pointer-events-none absolute -bottom-6 right-4 h-40 w-40 text-white/5 sm:h-52 sm:w-52"
          />

          <div className="relative px-5 py-7 sm:px-8 sm:py-9">
            <div className="flex items-center gap-3">
              <ChessPiece
                name="king"
                className="h-8 w-8 shrink-0 text-amber-300"
              />
              <div>
                <h2 className="text-2xl font-bold">Průběžné pořadí</h2>
                <p className="text-sm text-slate-300">
                  Body za umístění napříč celou sezonou
                </p>
              </div>
            </div>

            {topThree.length > 0 && (
              <div className="mt-6 grid gap-3 sm:grid-cols-3">
                {topThree.map((row, index) => {
                  const style = TOP_RANKS[index];
                  return (
                    <div
                      key={row.playerId}
                      className={`${style.stripe} rounded-2xl border border-white/10 bg-white/5 p-4 backdrop-blur-sm`}
                    >
                      <div className="flex items-center gap-2">
                        <ChessPiece
                          name={style.piece}
                          className={`h-6 w-6 shrink-0 ${style.pieceColor}`}
                        />
                        <span className="text-xs font-medium uppercase tracking-wide text-slate-300">
                          {style.label}
                        </span>
                      </div>
                      <p className="mt-2 text-xl font-semibold">
                        {row.playerName}
                      </p>
                      <p className="text-sm tabular-nums text-slate-300">
                        {row.placementPoints} bodů · {row.attended}{" "}
                        {row.attended === 1 ? "turnaj" : "turnajů"}
                      </p>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <div className="bg-white p-5">
          <StandingsTable standings={standings} tournaments={tournaments} />
        </div>
      </div>
    </section>
  );
};

export default ChampionshipBoard;
